import { useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { LoadingStatus } from 'src/constants/loading-status';
import { initCreateJiraIssue } from 'src/redux/jira/actions/create-jira-issue';
import {
  getCreateJiraIssuePreferences,
  getJiraIssueConnectedSites,
  getJiraIssueFetchedStatus,
} from 'src/redux/jira/selectors/jira-issue-selectors';

export type UseJiraCloudIdResult = {
  /**
   * cloudId of the Jira site to seed the create-work-item modal with. Prefers the
   * `default-jira-project` user preference when its site is still connected,
   * otherwise falls back to the first connected site.
   */
  cloudId: string | undefined;
  /**
   * projectId from the `default-jira-project` user preference, surfaced only
   * when the preferred site is still in the user's connected sites.
   */
  projectId: string | undefined;
  /** True while the connected-sites request is in flight. */
  isLoading: boolean;
  /** True if the user has at least one connected Jira site. */
  hasSites: boolean;
  /** Set when the connected-sites request fails. */
  error: string | undefined;
};

/**
 * Resolves a default Jira `cloudId`/`projectId` pair to seed a "create Jira
 * work item" modal with, using Bitbucket's existing redux flow:
 *
 * - Connected sites come from
 *   `/!api/internal/workspaces/{workspace}/jira/sites?connected=true`, fetched
 *   by `fetchConnectedJiraSitesSaga` (Bitbucket session auth).
 * - The default project comes from the `default-jira-project` user/repository
 *   preference loaded by `fetchCreateJiraIssuePreferencesSaga`.
 *
 * This hook is a pure reader: it does not trigger fetches. The owning page is
 * responsible for dispatching `initCreateJiraIssue()` once on mount (see
 * `useInitJiraCloudIdData`) so consumers in lists (e.g. per-row inline
 * actions) don't each fire a network request.
 */
export const useJiraCloudId = (): UseJiraCloudIdResult => {
  const connectedSites = useSelector(getJiraIssueConnectedSites);
  const fetchedStatus = useSelector(getJiraIssueFetchedStatus);
  const preferences = useSelector(getCreateJiraIssuePreferences);

  return useMemo(() => {
    const isLoading =
      fetchedStatus === LoadingStatus.Before ||
      fetchedStatus === LoadingStatus.Fetching;

    const preferredSite = preferences.value
      ? connectedSites.find(site => site.cloudId === preferences.value?.cloudId)
      : undefined;

    const cloudId = preferredSite?.cloudId ?? connectedSites[0]?.cloudId;
    const projectId = preferredSite ? preferences.value?.projectId : undefined;

    return {
      cloudId,
      projectId,
      isLoading,
      hasSites: connectedSites.length > 0,
      error:
        fetchedStatus === LoadingStatus.Failed
          ? 'Failed to load connected Jira sites'
          : undefined,
    };
  }, [connectedSites, fetchedStatus, preferences.value]);
};

/**
 * Dispatches `initCreateJiraIssue()` once on mount when connected sites
 * haven't been loaded yet. Call this from a page-level component that owns a
 * list of items so that per-row consumers of `useJiraCloudId` don't each
 * trigger a network request.
 */
export const useInitJiraCloudIdData = (): void => {
  const dispatch = useDispatch();
  const fetchedStatus = useSelector(getJiraIssueFetchedStatus);

  useEffect(() => {
    if (fetchedStatus === LoadingStatus.Before) {
      dispatch(initCreateJiraIssue());
    }
  }, [dispatch, fetchedStatus]);
};
